import React, { useEffect, useRef, useState } from 'react';
import { API, AppMobile, getFromLocalStorageByKey, LoggerInstance } from '@vfit/shared/data-access';
import {
  CONSUMER_CMS_SHOPPING_CART,
  GetCustomerResponse,
  ICommonLabelsInStepper,
  IGlobalConfigCMS,
  IUserService,
  IUserType,
  organizeCommonLabelsInStepper,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import { IStepCard, IStepperUtils } from '@vfit/shared/models';
import { StepperStateCards } from '@vfit/shared/components';
import { CheckoutModalContainer, OverlayGlobalStyle } from './checkoutEnergy.style';
import {
  getEnergyUserType,
  getMsisdn,
  getMsisdnOrCookie,
  organizeFlowsAppend,
} from './checkoutEnergy.utils';
import { ENERGY_FLOW, OTP_FLOW } from './checkoutEnergy.flow';
import { ID_FLOWS } from './checkoutEnergy.constants';
import { useEnergyCheckout } from '../energy.context';
import { ICheckoutEnergyModalProps, IEnergyPostData } from './checkoutEnergy.models';
import { HiddenPostForm } from '@vfit/consumer/components';

const CheckoutEnergy = ({
  handleClose,
  backgroundImage,
  isLoadingCart,
}: ICheckoutEnergyModalProps) => {
  const loggedUser: IUserService = getFromLocalStorageByKey('user');
  const loggedCustomer: GetCustomerResponse[] = getFromLocalStorageByKey('customerData');
  const isLoggedUser = getEnergyUserType() === IUserType.LOGGED_USER;
  const {
    owningData,
    findCaller,
    slidesGoBack,
    isDisabledBackButton,
    product,
    setSlidesGoBack,
    setIsModalButtonSticky,
  } = useEnergyCheckout();
  const findCallerRef = useRef(findCaller);
  const [energyPostData, setEnergyPostData] = useState<IEnergyPostData>({
    isEnabled: false,
    url: '',
    account: '',
    firstname: '',
    lastname: '',
    tel: '',
    visitor_id_asset_active: '',
    nof: '',
    page_environment: 'Web',
  });
  const stepperRef = useRef<IStepperUtils>();
  const flow = ENERGY_FLOW.DEFAULT;
  const [steps, setSteps] = useState<IStepCard[]>(flow);

  const globalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const commonLabels = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_STEPPER_COMMON_LABELS_MOBILE
  ) as ICommonLabelsInStepper;

  const { goBackInStepper } = organizeCommonLabelsInStepper(commonLabels);

  const isButtonStickyConfig = globalConfig?.globalconfig?.modals?.enableButtonSticky === 'true';
  setIsModalButtonSticky(isButtonStickyConfig);

  // region Append steps

  const updateActiveIndexOnRemoveCards = (organizedStep: IStepCard[]) => {
    const diffSteps = steps.length - organizedStep.length;
    if (diffSteps > 0 && stepperRef.current) {
      const diff = stepperRef.current._getActiveSlideIndex() - diffSteps;
      stepperRef.current._setActiveSlideIndex(diff < 0 ? 0 : diff);
    }
  };

  const appendOtp = () => {
    let organizedSteps = steps;
    const fromKeySteps = ID_FLOWS.PERSONAL_INFO;
    if (!findCaller.showOtp || AppMobile.checkIsApp()) {
      organizedSteps = organizeFlowsAppend(steps, false, fromKeySteps, OTP_FLOW.OTP);
    } else {
      organizedSteps = organizeFlowsAppend(
        steps,
        findCaller.showOtp || false,
        fromKeySteps,
        OTP_FLOW.OTP
      );
    }
    setSteps(organizedSteps);
    updateActiveIndexOnRemoveCards(organizedSteps);
  };

  // endregion

  const programmaticallyGoBackNext = async (
    numberSlides: number,
    type: 'NEXT' | 'BACK',
    onFinish?: () => void
  ) => {
    const delay = (time: number) => new Promise((resolve) => setTimeout(resolve, time));
    if (numberSlides !== -1) {
      // eslint-disable-next-line no-restricted-syntax
      for (const indexSlides of Array.from(Array(numberSlides - 1))) {
        LoggerInstance.debug('IndexSlides', indexSlides);
        if (type === 'NEXT') {
          stepperRef?.current?._stepperGoNext();
          await delay(100);
        } else {
          stepperRef?.current?._stepperGoPrev();
          await delay(600);
        }
      }
      setTimeout(() => {
        if (onFinish) {
          stepperRef?.current?._setActiveSlideIndex(numberSlides + 1);
          onFinish?.();
        }
      }, 150 * numberSlides);
    }
  };

  const handlePostEnergy = () => {
    const energyUserType = getEnergyUserType(findCallerRef?.current);
    const isActiveOrLogged =
      energyUserType === IUserType.NEXT_USER_ACTIVE || energyUserType === IUserType.LOGGED_USER;
    const isProspect = !isActiveOrLogged;

    const postEnergyUrl = isActiveOrLogged
      ? product?.energy?.postActiveUser || ''
      : product?.energy?.postInactiveUser || '';

    const individual = isLoggedUser
      ? loggedCustomer?.[0]?.owningIndividual
      : owningData?.owningIndividual || findCallerRef?.current?.individualRef;

    const postName = individual?.firstName || '';
    const postSurname = individual?.lastName || '';
    const postFiscalCode = individual?.fiscalCode || individual?.['fiscalcode'] || '';

    const msisdnOrCookie = getMsisdnOrCookie(findCallerRef?.current, loggedUser);
    const tel = getMsisdn(findCallerRef?.current, loggedUser);

    const nof = product?.energy?.nof;
    const shouldUseNof = !!nof;
    const shouldUseGotoCfg = product?.energy?.gotoCfg === 'true';

    const payload: IEnergyPostData = {
      isEnabled: true,
      url: postEnergyUrl,
      account: postFiscalCode,
      firstname: postName,
      lastname: postSurname,
      tel,
      visitor_id_asset_active: msisdnOrCookie,
      ispb: isProspect ? '1' : '0',
      page_environment: AppMobile?.checkIsApp() ? 'App' : 'Web',
    };

    if (shouldUseNof) {
      payload.nof = nof;
    }

    if (shouldUseGotoCfg && shouldUseNof) {
      payload.gotocfg = '1';
    }

    setEnergyPostData(payload);
  };

  const getEnergyPostParams = () => {
    const fields = [
      'account',
      'firstname',
      'lastname',
      'tel',
      'nof',
      'ispb',
      'visitor_id_asset_active',
      'page_environment',
      'gotocfg',
    ];

    return fields.reduce((params, field) => {
      const value = energyPostData?.[field];
      if (value) {
        params[field] = {
          name: field,
          value: value,
        };
      }
      return params;
    }, {} as Record<string, { name: string; value: string }>);
  };

  useEffect(() => {
    findCallerRef.current = findCaller;
    appendOtp();
  }, [findCaller]);

  useEffect(() => {
    const closeOnEscapeKey = (e: KeyboardEvent) => (e.key === 'Escape' ? handleClose() : null);
    document.body.addEventListener('keydown', closeOnEscapeKey);
    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKey);
    };
  }, [handleClose]);

  useEffect(() => {
    if (slidesGoBack && slidesGoBack !== -1 && stepperRef?.current) {
      programmaticallyGoBackNext(slidesGoBack, 'BACK');
      setSlidesGoBack(-1);
    }
  }, [slidesGoBack]);

  useEffect(() => {
    if (Object.keys(product)?.length > 0 && isLoggedUser) handlePostEnergy();
  }, [product]);

  return (
    <>
      {!isLoggedUser && (
        <>
          <OverlayGlobalStyle />
          <CheckoutModalContainer>
            <StepperStateCards
              ref={stepperRef}
              steps={steps}
              onBack={handleClose}
              onClose={handleClose}
              onFinish={handlePostEnergy}
              blurredImage={backgroundImage}
              hideBackButton={isDisabledBackButton}
              isLoadingStickyOffer={isLoadingCart}
              lottieLoader={product?.loaders?.coverageTool}
              goBackLabel={goBackInStepper}
            />
          </CheckoutModalContainer>
        </>
      )}
      {energyPostData?.isEnabled && (
        <HiddenPostForm url={energyPostData.url} params={getEnergyPostParams()} />
      )}
    </>
  );
};

export default CheckoutEnergy;
